import React, { useMemo } from 'react'

import { App, ConfigProvider } from 'antd'
import type { ThemeConfig } from 'antd'
import { ThemeProvider } from 'antd-style'

import { DESIGN_SYSTEM_PREFIX, designSystemTheme } from './config'
import { customTokens } from './customTokens'
import type { DesignSystemTokens } from './customTokens'

function mergeComponents(
  base: ThemeConfig['components'],
  overrides?: ThemeConfig['components']
): ThemeConfig['components'] {
  if (!overrides) {
    return base
  }
  const baseMap = (base ?? {}) as Record<string, object>
  const overridesMap = overrides as Record<string, object>
  const merged: Record<string, object> = { ...baseMap }
  for (const [key, value] of Object.entries(overridesMap)) {
    merged[key] = { ...(baseMap[key] ?? {}), ...value }
  }
  return merged as ThemeConfig['components']
}

interface Props {
  children: React.ReactNode
  themeOverrides?: ThemeConfig['token']
  customTokenOverrides?: Partial<DesignSystemTokens>
  componentOverrides?: ThemeConfig['components']
}

export const DesignSystemProvider: React.FC<Props> = ({
  children,
  themeOverrides,
  customTokenOverrides,
  componentOverrides
}) => {
  const theme = useMemo<ThemeConfig>(
    () => ({
      ...designSystemTheme,
      token: {
        ...designSystemTheme.token,
        ...themeOverrides
      },
      components: mergeComponents(
        designSystemTheme.components,
        componentOverrides
      ),
      inherit: false
    }),
    [themeOverrides, componentOverrides]
  )

  const mergedCustomTokens = useMemo(
    () => ({ ...customTokens, ...customTokenOverrides }),
    [customTokenOverrides]
  )

  return (
    <ConfigProvider prefixCls={DESIGN_SYSTEM_PREFIX} theme={theme}>
      <ThemeProvider
        customToken={mergedCustomTokens}
        prefixCls={DESIGN_SYSTEM_PREFIX}
      >
        <App>{children}</App>
      </ThemeProvider>
    </ConfigProvider>
  )
}
